import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Utils } from '@pbt/pbt-ui-components'

import {
  type CompletedSaleTransaction,
  type FailedSaleTransaction,
  useInitiateTerminalSaleMutation,
} from '~/api/graphql/generated/types'
import {
  type PaymentData,
  type PaymentDialogContextType,
  type PaymentDialogStateType,
  PaymentFlowState,
  PaymentMethod,
} from '~/constants/addPaymentDialogV2Constants'
import PaymentType from '~/constants/paymentTypes'
import {
  fetchBatchInvoice,
  refreshBalanceHistory,
} from '~/store/actions/finance'
import {
  createManualPayment,
  createPayment,
  linkUnappliedPayments,
} from '~/store/actions/payments'
import { getCurrentBusinessId } from '~/store/reducers/auth'
import { getPaymentMethod, getPaymentType } from '~/store/reducers/constants'
import { fetchInvoiceV3 } from '~/store/reducers/invoiceV3'
import {
  getPaymentsError,
  getPaymentsIsLoading,
} from '~/store/reducers/payments'
import type {
  InvoiceOrEstimate,
  PaymentPayload,
  UnsavedExtendPayment,
} from '~/types'
import usePrevious from '~/utils/usePrevious'

import { roundToTwoDecimals } from '../utils/clientAccountCreditUtils'
import { checkPaymentType } from '../utils/paymentDialogUtils'

// Helper function to handle payment success state based on partial/full payment
const handlePaymentSuccessState = (
  currentPaymentData: PaymentData,
  invoice: InvoiceOrEstimate,
  paymentDialogDispatch: React.Dispatch<any>,
) => {
  const invoiceDue = invoice.dueToPay || 0
  const paymentCheck = checkPaymentType(currentPaymentData, invoiceDue)

  if (paymentCheck.isPartialPayment) {
    // This is a partial payment
    paymentDialogDispatch({
      type: 'RESET_FOR_PARTIAL_PAYMENT',
      payload: {
        totalPaid: paymentCheck.totalPaymentAmount,
      },
    })
  } else {
    // This is a full payment
    paymentDialogDispatch({
      type: 'CLEAR_PARTIAL_PAYMENT',
    })
    paymentDialogDispatch({
      type: 'SET_STATE',
      payload: PaymentFlowState.PAYMENT_SUCCESS,
    })
  }
}

type PaymentDialogAction =
  | { payload: PaymentFlowState; type: 'SET_STATE' }
  | { type: 'CANCEL_TRANSACTION' }
  | { payload: PaymentData; type: 'SET_PAYMENT_DATA' }
  | { type: 'CLEAR_PAYMENT_DATA' }
  | { type: 'RESET_DIALOG' }
  | { payload: number; type: 'SET_PARTIAL_PAYMENT' }
  | { type: 'CLEAR_PARTIAL_PAYMENT' }
  | { payload: { totalPaid: number }; type: 'RESET_FOR_PARTIAL_PAYMENT' }
  | {
      payload: {
        message: string
        severity: 'error' | 'warning'
        status: number | string
      }
      type: 'SET_PAYMENT_ERROR'
    }
  | { type: 'CLEAR_PAYMENT_ERROR' }

type ActionHandlers = Record<
  PaymentDialogAction['type'],
  (
    state: PaymentDialogStateType,
    action: PaymentDialogAction,
  ) => PaymentDialogStateType
>

interface PaymentDialogProviderProps {
  children: ReactNode
  invoice: InvoiceOrEstimate
  isEstimate?: boolean
  refetchInvoice?: () => void
}
const createInitialDialogState = (): PaymentDialogStateType => ({
  currentState: PaymentFlowState.RECORD_PAYMENT,
  hasPartialPayment: false,
  partialPaymentAmount: 0,
  paymentError: null,
  paymentData: {
    amountToCharge: '0',
    paymentMethod: PaymentMethod.CREDIT_DEBIT as string,
    paymentTerminal: '',
    authNumber: undefined,
    creditCardLastFour: undefined,
    creditCardType: undefined,
    checkNumber: undefined,
    amountReceived: undefined,
    changeDue: undefined,
    invoiceIds: [],
    accountCreditTotal: 0,
    selectedAccountCreditIds: [],
  },
})

const actionHandlers: ActionHandlers = {
  SET_STATE: (state, action) => ({
    ...state,
    currentState: (action as { payload: PaymentFlowState }).payload,
  }),
  CANCEL_TRANSACTION: (state) => ({
    ...state,
    currentState: PaymentFlowState.RECORD_PAYMENT,
    paymentData: {
      ...state.paymentData,
      creditCardLastFour: undefined,
      creditCardType: undefined,
      checkNumber: undefined,
      amountReceived: undefined,
      changeDue: undefined,
    },
  }),
  RESET_DIALOG: (state) => ({
    ...state,
    currentState: PaymentFlowState.RECORD_PAYMENT,
    paymentData: {
      amountToCharge: '0',
      paymentMethod: PaymentMethod.CREDIT_DEBIT as string,
      paymentTerminal: '',
      authNumber: undefined,
      creditCardLastFour: undefined,
      creditCardType: undefined,
      checkNumber: undefined,
      amountReceived: undefined,
      changeDue: undefined,
      invoiceIds: [],
      accountCreditTotal: 0,
      selectedAccountCreditIds: [],
    },
    hasPartialPayment: false,
    partialPaymentAmount: 0,
  }),
  SET_PAYMENT_DATA: (state, action) => ({
    ...state,
    paymentData: {
      ...state.paymentData,
      ...(action as { payload: PaymentData }).payload,
    },
  }),
  CLEAR_PAYMENT_DATA: (state) => ({
    ...state,
    paymentData: {
      ...createInitialDialogState().paymentData,
      creditCardLastFour: undefined,
      creditCardType: undefined,
      authNumber: undefined,
      checkNumber: undefined,
      amountReceived: undefined,
      changeDue: undefined,
    },
  }),
  SET_PARTIAL_PAYMENT: (state, action) => ({
    ...state,
    hasPartialPayment: true,
    partialPaymentAmount: (action as { payload: number }).payload,
  }),
  CLEAR_PARTIAL_PAYMENT: (state) => ({
    ...state,
    hasPartialPayment: false,
    partialPaymentAmount: 0,
  }),
  RESET_FOR_PARTIAL_PAYMENT: (state, action) => {
    const { totalPaid } = (action as { payload: { totalPaid: number } }).payload
    return {
      ...state,
      currentState: PaymentFlowState.RECORD_PAYMENT,
      hasPartialPayment: true,
      partialPaymentAmount: totalPaid,
      paymentData: {
        ...createInitialDialogState().paymentData,
      },
    }
  },
  SET_PAYMENT_ERROR: (state, action) => ({
    ...state,
    paymentError: (
      action as {
        payload: {
          message: string
          severity: 'error' | 'warning'
          status: number
        }
      }
    ).payload,
  }),
  CLEAR_PAYMENT_ERROR: (state) => ({
    ...state,
    paymentError: null,
  }),
}

const paymentDialogReducer = (
  state: PaymentDialogStateType,
  action: PaymentDialogAction,
): PaymentDialogStateType => {
  const handler = actionHandlers[action.type]
  return handler ? handler(state, action) : state
}

const PaymentDialogContext = createContext<
  PaymentDialogContextType | undefined
>(undefined)

export const PaymentDialogProvider = ({
  children,
  invoice,
  isEstimate,
  refetchInvoice: _refetchInvoice,
}: PaymentDialogProviderProps) => {
  const [dialogState, paymentDialogDispatch] = useReducer(
    paymentDialogReducer,
    createInitialDialogState(),
  )

  const [isButtonActionLoading, setIsButtonActionLoading] = useState(false)
  const [pendingPaymentData, setPendingPaymentData] =
    useState<PaymentData | null>(null)
  const businessId = useSelector(getCurrentBusinessId) || ''
  const PaymentTypeConstant = useSelector(getPaymentType)
  const PaymentMethodConstant = useSelector(getPaymentMethod)
  const dispatch = useDispatch()

  // Redux state selectors for payment operations
  const paymentError = useSelector(getPaymentsError)
  const paymentLoading = useSelector(getPaymentsIsLoading)
  const wasPaymentLoading = usePrevious(paymentLoading)

  // Listen for payment success/failure states
  useEffect(() => {
    if (paymentError) {
      const errorMessage = paymentError

      paymentDialogDispatch({
        type: 'SET_PAYMENT_ERROR',
        payload: {
          message: errorMessage,
          severity: 'error',
          status: 500,
        },
      })

      setIsButtonActionLoading(false)
      setPendingPaymentData(null)
    } else if (
      !paymentLoading &&
      wasPaymentLoading &&
      !paymentError &&
      pendingPaymentData
    ) {
      // Payment completed successfully - handle success state
      handlePaymentSuccessState(
        pendingPaymentData,
        invoice,
        paymentDialogDispatch,
      )
      setIsButtonActionLoading(false)
      setPendingPaymentData(null)
    }
  }, [
    paymentError,
    paymentLoading,
    wasPaymentLoading,
    pendingPaymentData,
    invoice,
    paymentDialogDispatch,
  ])

  const PaymentMethodsToIdsMap = useMemo(
    () => ({
      [PaymentMethod.CASH.toString()]: `${Utils.findConstantIdByName('Cash', PaymentMethodConstant)}`,
      [PaymentMethod.CREDIT_DEBIT.toString()]: `${Utils.findConstantIdByName('Credit card', PaymentMethodConstant)}`,
      [PaymentMethod.CHECK.toString()]: `${Utils.findConstantIdByName('Check', PaymentMethodConstant)}`,
      [PaymentMethod.CARECREDIT.toString()]: `${Utils.findConstantIdByName('CareCredit', PaymentMethodConstant)}`,
    }),
    [PaymentMethodConstant],
  )

  const [initiateTerminalSaleMutation, { error }] =
    useInitiateTerminalSaleMutation()

  const actions = useMemo(
    () => ({
      sendToTerminal: async (currentPaymentData: PaymentData) => {
        paymentDialogDispatch({ type: 'CLEAR_PAYMENT_ERROR' })
        setIsButtonActionLoading(true)
        if (
          currentPaymentData.accountCreditTotal &&
          currentPaymentData.accountCreditTotal > 0 &&
          currentPaymentData.selectedAccountCreditIds &&
          currentPaymentData.selectedAccountCreditIds.length > 0
        ) {
          dispatch(
            linkUnappliedPayments(
              [invoice.id],
              currentPaymentData.selectedAccountCreditIds,
            ),
          )
          dispatch(fetchInvoiceV3({ id: invoice.id }))
          if (invoice.clientId) {
            dispatch(refreshBalanceHistory(invoice.clientId))
          }
        }

        const result = await initiateTerminalSaleMutation({
          variables: {
            input: {
              amount: parseFloat(currentPaymentData.amountToCharge || '0'),
              externalDeviceId: currentPaymentData.paymentTerminal || '',
              invoiceIds: invoice.id.split(','),
            },
          },
        })

        if (!result.data?.initiateTerminalSale || error) {
          // Cancel the transaction and set the error
          paymentDialogDispatch({
            type: 'CANCEL_TRANSACTION',
          })
          paymentDialogDispatch({
            type: 'SET_PAYMENT_ERROR',
            payload: {
              message: 'Failed to initiate terminal sale',
              severity: 'error',
              status: 500,
            },
          })
        } else {
          // Set the transaction data and move to payment request state
          paymentDialogDispatch({
            type: 'SET_STATE',
            payload: PaymentFlowState.PAYMENT_REQUEST,
          })
          paymentDialogDispatch({
            type: 'SET_PAYMENT_DATA',
            payload: {
              ...currentPaymentData,
              transactionId: result.data.initiateTerminalSale.id,
            },
          })
        }
        setIsButtonActionLoading(false)
      },
      recordPayment: async (currentPaymentData: PaymentData) => {
        paymentDialogDispatch({ type: 'CLEAR_PAYMENT_ERROR' })
        setIsButtonActionLoading(true)

        // If account credits applied are equal to or greater than the remaining balance, then we don't need to create a manual payment
        if (
          roundToTwoDecimals(currentPaymentData.accountCreditTotal || 0) >=
            roundToTwoDecimals(invoice.dueToPay || 0) &&
          currentPaymentData.selectedAccountCreditIds &&
          currentPaymentData.selectedAccountCreditIds.length > 0
        ) {
          // For multiple invoices, extract individual IDs from the combined ID
          const invoiceIdsForLinking = invoice.id.split(',')

          dispatch(
            linkUnappliedPayments(
              invoiceIdsForLinking,
              currentPaymentData.selectedAccountCreditIds,
            ),
          )

          if (!invoice.id.includes(',')) {
            dispatch(fetchInvoiceV3({ id: invoice.id }))
          }
          if (invoice.clientId) {
            dispatch(refreshBalanceHistory(invoice.clientId))
          }

          // Clear any partial payment state since this is a full payment
          paymentDialogDispatch({
            type: 'CLEAR_PARTIAL_PAYMENT',
          })
          // Move to success state
          paymentDialogDispatch({
            type: 'SET_STATE',
            payload: PaymentFlowState.PAYMENT_SUCCESS,
          })
          return
        }

        const { clientId } = invoice

        if (isEstimate) {
          const estimatePayment: Partial<UnsavedExtendPayment> = {
            amount: parseFloat(currentPaymentData.amountToCharge || '0'),
            invoiceId: invoice.id,
            invoiceIds: [],
            notes:
              currentPaymentData.paymentMethod === PaymentMethod.CHECK
                ? currentPaymentData.checkNumber || ''
                : currentPaymentData.paymentMethod === PaymentMethod.CARECREDIT
                  ? currentPaymentData.authNumber || ''
                  : '',
            paidByPersonId: invoice.clientId ?? undefined,
            paymentCardTypeId: null,
            paymentMethodId:
              PaymentMethodsToIdsMap[currentPaymentData.paymentMethod],
            paymentTypeId: Utils.findConstantIdByName(
              PaymentType.DEPOSIT,
              PaymentTypeConstant,
            ),
            cashReceived:
              currentPaymentData.paymentMethod === PaymentMethod.CASH
                ? parseFloat(currentPaymentData.amountReceived || '0')
                : undefined,
            cashChangeGiven:
              currentPaymentData.paymentMethod === PaymentMethod.CASH
                ? parseFloat(currentPaymentData.changeDue || '0')
                : undefined,
            clientId: clientId || '',
            businessId,
          }

          setPendingPaymentData(currentPaymentData)
          dispatch(createPayment(clientId, estimatePayment))
        } else {
          // For multiple invoices, extract individual IDs from the combined ID
          const invoiceIds = invoice.id.split(',')

          const paymentPayload: PaymentPayload = {
            aggregatedInvoiceTotal: invoice.dueToPay,
            aggregatedPaymentTotal: currentPaymentData.accountCreditTotal || 0,
            invoiceIds,
            paymentIds: currentPaymentData.selectedAccountCreditIds || [],
            requestedPayment: {
              amount: parseFloat(currentPaymentData.amountToCharge || '0'),
              paymentMethodId:
                PaymentMethodsToIdsMap[currentPaymentData.paymentMethod],
              paymentTypeId: Utils.findConstantIdByName(
                PaymentType.PAYMENT,
                PaymentTypeConstant,
              ),
              paidByPersonId: invoice.clientId ?? undefined,
              notes:
                currentPaymentData.paymentMethod === PaymentMethod.CHECK
                  ? currentPaymentData.checkNumber
                  : currentPaymentData.paymentMethod ===
                      PaymentMethod.CARECREDIT
                    ? currentPaymentData.authNumber
                    : '',
              cashReceived:
                currentPaymentData.paymentMethod === PaymentMethod.CASH
                  ? parseFloat(currentPaymentData.amountReceived || '0')
                  : undefined,
              cashChangeGiven:
                currentPaymentData.paymentMethod === PaymentMethod.CASH
                  ? parseFloat(currentPaymentData.changeDue || '0')
                  : undefined,
            },
          }

          setPendingPaymentData(currentPaymentData)
          dispatch(createManualPayment(clientId, paymentPayload))
        }
      },
      handleTransactionSuccess: (
        currentPaymentData: PaymentData,
        transaction: CompletedSaleTransaction,
      ) => {
        paymentDialogDispatch({
          type: 'SET_PAYMENT_DATA',
          payload: {
            ...currentPaymentData,
            creditCardLastFour: transaction.cardDetails?.lastFour,
            creditCardType: transaction.cardDetails?.type?.name,
          },
        })
        handlePaymentSuccessState(
          currentPaymentData,
          invoice,
          paymentDialogDispatch,
        )
        // Refresh the invoice after a successful payment
        if (!invoice.id.includes(',')) {
          dispatch(fetchInvoiceV3({ id: invoice.id }))
        } else {
          const invoiceIds = invoice.id.split(',')
          dispatch(fetchBatchInvoice(invoiceIds))
        }

        // Refresh balance history
        if (invoice.clientId) {
          dispatch(refreshBalanceHistory(invoice.clientId))
        }
      },
      handleTransactionFailure: (transaction: FailedSaleTransaction) => {
        // TODO: Handle Transaction Failure using error codes from the transaction response
        // Pull error from transaction.error.failureReason need to map to a human readable message
        paymentDialogDispatch({
          type: 'CANCEL_TRANSACTION',
        })
        paymentDialogDispatch({
          type: 'SET_PAYMENT_ERROR',
          payload: {
            message: 'Transaction failed',
            severity: 'error',
            status: transaction.error.failureReason.id,
          },
        })
      },
      handleTransactionHardFailure: () => {
        paymentDialogDispatch({
          type: 'CANCEL_TRANSACTION',
        })
        paymentDialogDispatch({
          type: 'SET_PAYMENT_ERROR',
          payload: {
            message: 'Transaction failed',
            severity: 'error',
            status: 500,
          },
        })
      },
      cancelTransaction: () =>
        paymentDialogDispatch({
          type: 'CANCEL_TRANSACTION',
        }),
      resetDialog: () =>
        paymentDialogDispatch({
          type: 'RESET_DIALOG',
        }),
      setPaymentData: (data: PaymentData) =>
        paymentDialogDispatch({ type: 'SET_PAYMENT_DATA', payload: data }),
      clearPaymentData: () =>
        paymentDialogDispatch({ type: 'CLEAR_PAYMENT_DATA' }),
      setPartialPayment: (amount: number) =>
        paymentDialogDispatch({ type: 'SET_PARTIAL_PAYMENT', payload: amount }),
      clearPartialPayment: () =>
        paymentDialogDispatch({ type: 'CLEAR_PARTIAL_PAYMENT' }),
      setPaymentError: (error: {
        message: string
        severity: 'error' | 'warning'
        status: number
      }) =>
        paymentDialogDispatch({ type: 'SET_PAYMENT_ERROR', payload: error }),
      clearPaymentError: () =>
        paymentDialogDispatch({ type: 'CLEAR_PAYMENT_ERROR' }),
    }),
    [
      businessId,
      invoice.clientId,
      invoice.id,
      invoice.dueToPay,
      dispatch,
      PaymentMethodConstant,
      PaymentTypeConstant,
      isEstimate,
    ],
  )

  const contextValue = useMemo(
    () => ({
      dialogState,
      invoice,
      actions,
      isButtonActionLoading,
      businessId,
      isEstimate,
    }),
    [
      dialogState,
      invoice,
      actions,
      isButtonActionLoading,
      businessId,
      isEstimate,
    ],
  )

  return (
    <PaymentDialogContext.Provider value={contextValue}>
      {children}
    </PaymentDialogContext.Provider>
  )
}

export const usePaymentDialog = (): PaymentDialogContextType => {
  const context = useContext(PaymentDialogContext)
  if (!context) {
    throw new Error(
      'usePaymentDialog must be used within a PaymentDialogProvider',
    )
  }
  return context
}
